import { NoticeType, PerformanceNotice, ProcessStatePatch, RefreshTier, ResourceKey } from "./performance-realtime.model";

/**
 * Funciones puras del tiempo real de Desempeño (design.md §7.2, §8.1). No
 * dependen de Angular ni de `EchoService`: reciben datos y devuelven datos,
 * para poder probarlas sin montar nada.
 */

/**
 * Claves de recurso por tipo de aviso (tabla de design.md §7.2). Desde C4
 * (design.md §4.12, ADR-11), `process.stage.launched`/`.reopened`/
 * `process.closed` no tienen entrada acá: ya no llegan como `PerformanceNotice`
 * de nivel superior por `.notification()` — viajan dentro de
 * `ProcessStateChanged.notices` y los muestra `notifyStageNotices()`, no este
 * despacho por clave.
 */
const RESOURCE_KEYS_BY_TYPE: Partial<Record<NoticeType, (processId: string) => ResourceKey[]>> = {
	ProcessStateChanged: (processId) => [`process:${processId}`, "home"],
	ProcessAssigned: () => ["home"],
	ObjectivesApprovalChanged: (processId) => [`objectives:${processId}`],
	"process.objectives.approval": (processId) => [`objectives:${processId}`],
	"process.tracking.reviewed": (processId) => [`tracking:${processId}`],
	"process.tracking.submitted": (processId) => [`team-tracking:${processId}`],
	"process.feedback.scheduled": (processId) => [`my-feedback:${processId}`],
	FeedbackIssued: (processId) => [`my-feedback:${processId}`],
	"process.review.issued": (processId) => [`my-review:${processId}`],
	"process.review.self_completed": (processId) => [`team-review:${processId}`],
};

/**
 * Claves de recurso afectadas por un aviso. Un aviso sin entrada en la tabla
 * (solo aviso, sin parche ni recarga: `process.tracking.pending`, `.silence`,
 * `process.stage.task.pending`, `MeetingUpdated`) no genera ninguna clave, y
 * por lo tanto ninguna petición.
 */
export function resourceKeysOf(notice: PerformanceNotice): ResourceKey[] {
	if (notice.type === "WorkspaceAppRefresh") return ["announcements"];

	const build = RESOURCE_KEYS_BY_TYPE[notice.type];
	if (!build) return [];

	const processId = notice.process_id === null || notice.process_id === undefined ? "" : String(notice.process_id);
	return build(processId);
}

/** Tipos de aviso masivos: llegan a todo un proceso (design.md §8.1). */
const PROCESS_TIER_TYPES = new Set<NoticeType>(["ProcessStateChanged", "ProcessAssigned"]);

/**
 * Ventana de jitter que le corresponde a un aviso: `workspace` para lo que
 * llega a todo el workspace, `process` para lo que llega a todo un proceso, y
 * `personal` para el resto (uno o pocos destinatarios).
 */
export function tierOf(notice: PerformanceNotice): RefreshTier {
	if (notice.type === "WorkspaceAppRefresh") return "workspace";
	if (PROCESS_TIER_TYPES.has(notice.type)) return "process";
	return "personal";
}

/**
 * Combina el estado de `ProcessStateChanged` con el proceso que la app ya
 * tiene (design.md §4.11, §7.4; C2, reemplaza a `applyProcessSnapshot()` de
 * A1 — hallazgo C1.6: reemplazar la lista de etapas entera con un estado
 * incompleto borraba nombres y posiciones en pantalla).
 *
 * `status`, `progress_stage` y `progress_stage_status` del proceso se toman
 * del evento (son del proceso, no de una etapa, y viajan completos). Cada
 * etapa se busca **por `id`**: del evento se toman solo `status`,
 * `started_at`, `finished_at`, `schedule_starts_at` y `schedule_finishes_at`;
 * el resto de la etapa (`stage{id,code,name,position}`, `active`…) se
 * conserva tal cual la app ya la tenía.
 *
 * **No inventa:** si el evento trae un `id` de etapa que la app no tiene, o a
 * la app le queda una etapa sin su par en el evento, devuelve `null` — nunca
 * arma una etapa a medias. Quien la llama debe entonces pedir la recarga
 * acotada (R19).
 *
 * La cota es `object`, no `Record<string, unknown>`: una `interface` real
 * (`ProcessAppListItem`, `PerformanceProcess`) nunca es asignable a un tipo
 * con firma de índice (TS2345, hallazgo de la tarea A3) aunque tenga
 * exactamente esas llaves. El `as unknown as T` es seguro: el objeto
 * devuelto tiene las mismas llaves de `process`, con las etapas combinadas
 * una por una.
 */
export function mergeProcessState<T extends { stages: unknown[] }>(process: T, state: ProcessStatePatch): T | null {
	const stateByStageId = new Map(state.stages.map((stage) => [String(stage.id), stage]));
	const matchedIds = new Set<string>();

	const mergedStages: unknown[] = [];
	for (const stage of process.stages as Array<Record<string, unknown>>) {
		const key = String(stage["id"]);
		const stagePatch = stateByStageId.get(key);
		if (!stagePatch) return null; // a la app le queda una etapa sin su par en el evento

		matchedIds.add(key);
		mergedStages.push({
			...stage,
			status: stagePatch.status,
			started_at: stagePatch.started_at,
			finished_at: stagePatch.finished_at,
			schedule_starts_at: stagePatch.schedule_starts_at,
			schedule_finishes_at: stagePatch.schedule_finishes_at,
		});
	}

	if (matchedIds.size !== stateByStageId.size) return null; // el evento trae un id que la app no tiene

	return {
		...process,
		status: state.status,
		progress_stage: state.progress_stage,
		progress_stage_status: state.progress_stage_status,
		stages: mergedStages,
	} as unknown as T;
}

/**
 * true solo si `path` empieza con `/performance/`: el `data` de un push o el
 * `app_path` de un aviso no pueden mandar a la app a una ruta arbitraria
 * (design.md §7.6).
 */
export function isSafeAppPath(path: string | null | undefined): boolean {
	return typeof path === "string" && path.startsWith("/performance/");
}

/** Ventanas de jitter en milisegundos, por tier (design.md §8.1). */
const JITTER_WINDOWS_MS: Record<RefreshTier, readonly [number, number]> = {
	personal: [250, 1500],
	process: [0, 15000],
	workspace: [0, 30000],
};

/**
 * Retraso aleatorio dentro de la ventana del tier, para repartir en el tiempo
 * a los N clientes que reciben el mismo evento. `random` es inyectable: en
 * producción es `Math.random`, y en las pruebas un valor fijo.
 */
export function jitterMs(tier: RefreshTier, random: () => number = Math.random): number {
	const [min, max] = JITTER_WINDOWS_MS[tier];
	return Math.round(min + random() * (max - min));
}

/**
 * Arma un `PerformanceNotice` desde una Notification recibida por
 * `.notification()` (campos comunes de `toBroadcast()`, design.md §5.1). El
 * `patch` conserva la forma que trae cada tipo (design.md §5.2); quien lo
 * consume decide cómo leerlo según `type`.
 */
export function noticeFromNotification(notification: Record<string, unknown>): PerformanceNotice {
	return {
		type: notification["type"] as NoticeType,
		process_id: (notification["process_id"] as number | string | null | undefined) ?? null,
		process_name: (notification["process_name"] as string | null | undefined) ?? null,
		title: (notification["title"] as string | null | undefined) ?? null,
		stage_id: (notification["stage_id"] as number | string | null | undefined) ?? null,
		stage_code: (notification["stage_code"] as string | null | undefined) ?? null,
		app_path: (notification["app_path"] as string | null | undefined) ?? null,
		patch: notification["patch"] ?? null,
	};
}

/**
 * Arma un `PerformanceNotice` desde un `UserAppNotified` (`.listen('.<Tipo>')`,
 * `WorkspaceAppRefresh` o `ProcessStateChanged` del canal del proceso). `name`
 * puede llegar con el punto inicial de `broadcastAs()` (design.md §7.2); se
 * normaliza sin él. El payload de cada tipo trae su propia forma (design.md
 * §5.2): `ObjectivesApprovalChanged` y `ProcessStateChanged` lo traen a nivel
 * raíz del payload (por eso `payload["process"] ?? payload` cae a `payload`
 * para los dos).
 */
export function noticeFromUserEvent(name: string, payload: Record<string, unknown>): PerformanceNotice {
	const type = (name.startsWith(".") ? name.slice(1) : name) as NoticeType;

	return {
		type,
		process_id: (payload["process_id"] as number | string | null | undefined) ?? null,
		process_name: (payload["process_name"] as string | null | undefined) ?? null,
		title: (payload["title"] as string | null | undefined) ?? null,
		stage_id: (payload["stage_id"] as number | string | null | undefined) ?? null,
		stage_code: (payload["stage_code"] as string | null | undefined) ?? null,
		app_path: (payload["app_path"] as string | null | undefined) ?? null,
		patch: payload["process"] ?? payload,
	};
}
